
import React, { createContext, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from "@/components/ui/use-toast";

type AuthContextType = {
  isAuthenticated: boolean;
  isAdmin: boolean;
  username: string;
  userCredits: number;
  login: (username: string, password: string) => void;
  logout: () => void;
  setUserCredits: (credits: number | ((prevCredits: number) => number)) => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [username, setUsername] = useState("");
  const [userCredits, setUserCreditsState] = useState(50);
  const navigate = useNavigate();

  // Wrapper function to handle both direct number and functional updates
  const setUserCredits = (credits: number | ((prevCredits: number) => number)) => {
    if (typeof credits === 'function') {
      setUserCreditsState(prevCredits => credits(prevCredits));
    } else {
      setUserCreditsState(credits);
    }
  };

  const showWelcomeMessage = () => {
    toast({
      title: "Καλώς όρισες",
      description: "Μπαίνοντας σε μια νέα κοινότητα είναι λίγο άβολα τα πράγματα. Το ξέρω. Είναι σαν να πηγαίνεις σε ένα party που δεν ξέρεις κανένα. Μπορούμε όμως να κάνουμε μερικά πράγματα για να το βελτιώσουμε αυτό.",
      duration: 10000,
    });
    setTimeout(() => {
      toast({
        title: "Προτάσεις",
        description: "Φτιάξε το προφίλ. Βάλε φωτογραφία, και γράψε μερικές πληροφορίες για εσένα. Τα άλλα μέλη τα ψάχνουν αυτά και είναι σημαντικό για να σε γνωρίσουν.",
        duration: 10000,
      });
    }, 10000);
    setTimeout(() => {
      toast({
        title: "Κοινότητα",
        description: "Η κοινότητα δεν είναι ένα πρόσωπο. Είναι η ανταλλαγή ενέργειας, απόψεων και προβληματισμών από όλους μας. Και πάλι, welcome 🙂",
        duration: 10000,
      });
    }, 20000);
  };

  const login = (username: string, password: string) => {
    // Admin login
    if (username === "admin" && password === "admin") {
      setIsAuthenticated(true);
      setIsAdmin(true);
      setUsername(username);
      setUserCredits(999);
      toast({
        title: "Επιτυχής Σύνδεση",
        description: "Έχετε συνδεθεί ως διαχειριστής",
      });
      navigate("/admin");
      
      if (!localStorage.getItem('welcomeShown_' + username)) {
        setTimeout(() => {
          showWelcomeMessage();
          localStorage.setItem('welcomeShown_' + username, 'true');
        }, 1000);
      }
    } 
    // Demo user login
    else if (username === "demo" && password === "demo") {
      setIsAuthenticated(true);
      setIsAdmin(false);
      setUsername(username);
      setUserCredits(50); // Default credits for demo user
      toast({
        title: "Επιτυχής Σύνδεση",
        description: "Έχετε συνδεθεί ως demo χρήστης",
      });
      navigate("/");
    }
    else {
      // Failed login attempt
      toast({
        title: "Σφάλμα",
        description: "Λάθος στοιχεία σύνδεσης",
        variant: "destructive"
      });
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setIsAdmin(false);
    setUsername("");
    navigate("/");
    toast({
      title: "Αποσύνδεση",
      description: "Έχετε αποσυνδεθεί επιτυχώς",
    });
  };

  return (
    <AuthContext.Provider 
      value={{ 
        isAuthenticated, 
        isAdmin,
        username,
        userCredits, 
        login, 
        logout, 
        setUserCredits 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
